import React, { useEffect, useRef, useState } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CircularProgress from "@mui/material/CircularProgress";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Alert from "@mui/material/Alert";
import TabContext from "@mui/lab/TabContext";
import TabPanel from "@mui/lab/TabPanel";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { firebaseAuth } from "../../components/firebase/auth";
import DashBoardDocuments from "./DashboardDocuments";
import DashBoardControl from "./DashboardControl";
import DashBoardOverview from "./DashBoardOverview";
import DashBoardFreezeDry from "./DashBoardFreezeDry";
import DashBoardFreezeDryProducts from "./DashBoardFreezeDryProducts";
import DashBoardFreezeDryPackages from "./DashBoardFreezeDryPackages";
import DashBoardFreezeDryStock from "./DashBoardFreezeDryStock";
import DashBoardFreshProducts from "./DashBoardFreshProducts";
import DashBoardFreshPackages from "./DashBoardFreshPackages";
import DashBoardFreshBatches from "./DashBoardFreshBatches";
import DashBoardFreshStock from "./DashBoardFreshStock";
import DashBoardCustomers from "./DashBoardCustomers";
import DashBoardWeeklyOrders from "./DashBoardWeeklyOrders";
import DashBoardWeeklyPlanning from "./DashBoardWeeklyPlanning";
import DashboardWebshopSettings from "./DashboardWebshopSettings";
import WebshopCatalog from "./webshop/WebshopCatalog";
import WebshopInventory from "./webshop/WebshopInventory";
import WebshopOverview from "./webshop/WebshopOverview";
import WebshopCaseQueue from "./webshop/WebshopCaseQueue";
import WebshopOrders from "./webshop/WebshopOrders";
import WebshopShipping from "./webshop/WebshopShipping";
import { ShopApiError, shopAdminApi, type ShopMe } from "./webshop/shopAdminApi";
import Labels from "./labels/Labels";
import { dashboardFallbackPath, dashboardGroups, dashboardPath, dashboardTabForPath } from "./dashboardRoutes";
import "./dashboard.css";

const dashboardComponents: Record<string, React.ComponentType> = {
  "19": DashBoardOverview, "1": Labels,
  "17": DashBoardWeeklyPlanning, "16": DashBoardWeeklyOrders,
  "11": DashBoardFreshProducts, "13": DashBoardFreshBatches, "12": DashBoardFreshPackages, "14": DashBoardFreshStock,
  "9": DashBoardFreezeDry, "20": DashBoardFreezeDryProducts, "10": DashBoardFreezeDryPackages, "18": DashBoardFreezeDryStock,
  "21": DashboardWebshopSettings, "23": WebshopCatalog, "26": WebshopInventory,
  "15": DashBoardCustomers, "5": DashBoardControl, "7": DashBoardDocuments,
};

const theme = createTheme({ palette: { primary: { main: "#2f7d61" } } });

export default function DashBoard() {
  const [user, loading] = useAuthState(firebaseAuth);
  const navigate = useNavigate();
  const location = useLocation();
  const [lastVisited, setLastVisited] = useState<Record<string, string>>({});
  const [shopAccess, setShopAccess] = useState<ShopMe | null>(null);
  const [shopAccessError, setShopAccessError] = useState<ShopApiError | null>(null);
  const [shopAccessLoading, setShopAccessLoading] = useState(true);
  const groupListRef = useRef<HTMLDivElement>(null);
  const activeRoute = dashboardTabForPath(location.pathname);
  const activeGroup = activeRoute?.group || dashboardGroups[0];
  const tabValue = activeRoute?.tab.value || activeGroup.tabs[0].value;
  const activeGroupId = activeRoute?.group.id;
  const activeTabValue = activeRoute?.tab.value;
  const shopAccessRefreshKey = location.pathname.startsWith("/webshop") ? location.pathname : "outside-webshop";
  const visibleGroups = shopAccess?.capabilities.includes("shop.read") ? dashboardGroups : dashboardGroups.filter((group) => group.id !== "webshop");

  useEffect(() => { if (!loading && !user) navigate("/login", { replace: true, state: { from: location.pathname } }); }, [loading, user, navigate, location.pathname]);
  useEffect(() => {
    if (!user) return;
    let live = true;
    setShopAccessLoading(true);
    shopAdminApi.me(user).then((value) => {
      if (live) { setShopAccess(value); setShopAccessError(null); setShopAccessLoading(false); }
    }).catch((error: unknown) => {
      if (live) { setShopAccess(null); setShopAccessError(error instanceof ShopApiError ? error : new ShopApiError(0, "network_error")); setShopAccessLoading(false); }
    });
    return () => { live = false; };
  }, [user, shopAccessRefreshKey]);
  useEffect(() => {
    if (activeGroupId && activeTabValue) setLastVisited((current) => current[activeGroupId] === activeTabValue ? current :
      { ...current, [activeGroupId]: activeTabValue });
  }, [activeGroupId, activeTabValue]);
  useEffect(() => {
    const keepActiveGroupVisible = () => {
      const list = groupListRef.current;
      const selected = list?.querySelector<HTMLElement>(".DashboardGroupButton.isActive");
      if (!list || !selected) return;
      const listBounds = list.getBoundingClientRect();
      const selectedBounds = selected.getBoundingClientRect();
      if (selectedBounds.left < listBounds.left) list.scrollLeft -= listBounds.left - selectedBounds.left + 8;
      else if (selectedBounds.right > listBounds.right) list.scrollLeft += selectedBounds.right - listBounds.right + 8;
    };
    keepActiveGroupVisible();
    window.addEventListener("resize", keepActiveGroupVisible);
    return () => window.removeEventListener("resize", keepActiveGroupVisible);
  }, [activeGroup.id, loading]);

  const selectGroup = (group: typeof dashboardGroups[number]) => {
    if (group.id !== activeGroup.id) {
      const tab = group.tabs.find((item) => item.value === lastVisited[group.id]) || group.tabs[0];
      navigate(dashboardPath(group, tab));
    }
  };
  const selectTab = (_event: React.SyntheticEvent, value: string) => {
    const tab = activeGroup.tabs.find((item) => item.value === value);
    if (tab) navigate(dashboardPath(activeGroup, tab));
  };

  if (!activeRoute && !loading && user) {
    if (location.pathname.startsWith("/webshop/")) return <div className="DashBoardTabInner"><Alert severity="warning">Sidan finns inte i Webshop. Öppna ett Webshop-avsnitt via navigationen.</Alert></div>;
    return <Navigate to={dashboardFallbackPath(location.pathname)} replace />;
  }
  if (activeGroup.id === "webshop" && !loading && user) {
    if (shopAccessLoading) return <div className="DashboardLoading" role="status"><CircularProgress color="inherit" /></div>;
    if (!shopAccess?.capabilities.includes("shop.read")) return <div className="DashBoardTabInner"><Alert severity="error">{shopAccessError?.status === 403 ? "Du har inte behörighet till Webshop." : "Webshop-behörighet kunde inte verifieras. Kontrollera API-anslutningen och försök igen."}</Alert></div>;
  }

  return <div className="DashBoard">
    {loading || !user ? <div className="DashboardLoading" role="status" aria-label="Laddar dashboard"><CircularProgress color="inherit" /></div> : <ThemeProvider theme={theme}>
      <TabContext value={tabValue}>
        <div className="DashBoardInner">
          <div className="DashBoardInnerTabs">
            <nav className="DashboardNavigation" aria-label="Dashboardens navigation">
              <div className="DashboardGroupBar">
                <span className="DashboardNavCaption">Arbetsområden</span>
                <div className="DashboardGroupList" role="group" aria-label="Välj arbetsområde" ref={groupListRef}>
                  {visibleGroups.map((group) => <button key={group.id} type="button" className={`DashboardGroupButton${group.id === activeGroup.id ? " isActive" : ""}`} aria-pressed={group.id === activeGroup.id} onClick={() => selectGroup(group)}><span>{group.label}</span><small>{group.tabs.length}</small></button>)}
                </div>
              </div>
              <div className="DashboardTabBar">
                <span className="DashboardTabBarLabel">{activeGroup.label}</span>
                <Tabs className="DashboardTabs" value={tabValue} onChange={selectTab} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile aria-label={`Flikar i ${activeGroup.label}`}>
                  {activeGroup.tabs.map((tab) => <Tab key={tab.value} value={tab.value} label={tab.label} id={`dashboard-tab-${tab.value}`} aria-controls={`dashboard-panel-${tab.value}`} />)}
                </Tabs>
              </div>
            </nav>
            {activeGroup.id === "webshop" && <Alert severity="info">Miljö: {shopAccess?.environment} · Firebase-projekt: {shopAccess?.projectId}{shopAccess?.previewOnly ? ". Webbshoppriser och andra serveråtgärder blir tillgängliga när shopAdminApi är driftsatt." : ""}</Alert>}
            {dashboardGroups.flatMap((group) => group.tabs).map((tab) => {
              const Component = dashboardComponents[tab.value];
              return <TabPanel className="DashboardPanel" sx={{ p: 0 }} key={tab.value} value={tab.value} id={`dashboard-panel-${tab.value}`} aria-labelledby={`dashboard-tab-${tab.value}`}>
                {tab.value === "21" ? <DashboardWebshopSettings canManage={shopAccess?.capabilities.includes("shop.settings.manage")} previewOnly={shopAccess?.previewOnly} />
                  : tab.value === "22" ? <WebshopOverview previewOnly={shopAccess?.previewOnly} />
                  : tab.value === "23" ? <WebshopCatalog canManage={shopAccess?.capabilities.includes("shop.catalog.manage")} previewOnly={shopAccess?.previewOnly} />
                  : tab.value === "24" ? <WebshopOrders canRead={shopAccess?.capabilities.includes("shop.fulfilment.manage")} previewOnly={shopAccess?.previewOnly} />
                  : tab.value === "26" ? <WebshopInventory canManage={shopAccess?.capabilities.includes("shop.inventory.manage")} canReadOrders={shopAccess?.capabilities.includes("shop.fulfilment.manage")} previewOnly={shopAccess?.previewOnly} />
                  : tab.value === "29" ? <WebshopShipping canManage={shopAccess?.capabilities.includes("shop.settings.manage")}
                    canAssign={shopAccess?.capabilities.includes("shop.catalog.manage")} previewOnly={shopAccess?.previewOnly} />
                  : tab.value === "27" || tab.value === "28" ? <WebshopCaseQueue kind={tab.value === "27" ? "support" : "withdrawal"}
                    canReadOrders={shopAccess?.capabilities.includes("shop.fulfilment.manage")}
                    canManage={shopAccess?.capabilities.includes("shop.support.manage")} previewOnly={shopAccess?.previewOnly} />
                  : Component ? <Component /> : <div className="DashBoardTabInner"><p>Det här Webshop-avsnittet öppnas när dess skyddade API och arbetsflöde är färdigt.</p></div>}
              </TabPanel>;
            })}
          </div>
        </div>
      </TabContext>
    </ThemeProvider>}
  </div>;
}
